import React, { createContext, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { makeSelectCurrentUser } from '../ui/containers/App/selectors';
import { fetchConfig } from '../utils/fetchConfig';

export const ConfigContext = createContext();

/**
 * ConfigProvider component that fetches and provides configuration data
 * to its children components via context.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to render.
 * @returns {JSX.Element} The ConfigProvider component.
 */
export const ConfigProvider = ({ children }) => {
  const [envConfig, setEnvConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const userData = useSelector(makeSelectCurrentUser());
  
  // Memoize userData to prevent unnecessary re-renders
  const memoizedUserData = useMemo(() => userData, [userData?.token, userData?.user?.id]);

  useEffect(() => {
    let isMounted = true;
    
    const loadConfig = async () => {
      if (!memoizedUserData?.token) {
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const envData = await fetchConfig(memoizedUserData);
        if (isMounted) {
          setEnvConfig(envData);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to load configuration');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, [memoizedUserData]);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(() => ({
    config: envConfig,
    loading,
    error,
    hasConfig: Object.keys(envConfig).length > 0
  }), [envConfig, loading, error]);

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
};
